import { Typography, TYPOGRAPHY_WEIGHT } from '../../../components/Typography'
import { Input } from '../../../components/Input'
import { PLACEHOLDERS } from '../../../constants'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import DeleteAction from './DeleteAction'
import AddAction from './AddAction'
import { Table } from '../../../components/Table'
import { isTableHasParams } from '../../../helper/common'
import {
  TPRSFacilityUsersData,
  TPRSFacilityUsersFilters,
  TPRSFacilityUsersFiltersFunc
} from './types'
import { columns } from './constants'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from '../../../redux/store/user/getters'
import { ACTIONS, SUBJECTS } from '../Abilities'
import { subject } from '@casl/ability'
import {
  Can,
  usePrsDetailsAbility
} from '../PRSDetails/Providers/PrsDetailsAbilityProvider'
import { usePRSDetailsContext } from '../PRSDetails/Providers/PRSDetailsContextProvider'
import { useEffect, useRef, useState } from 'react'
import {
  THealthSystemTableRequestData,
  THealthSystemUsersParams
} from '../../HealthSystemUsers/types'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import { useParams } from 'react-router-dom'
import axios, { CancelTokenSource } from 'axios'
import { useDebounce } from '../../../hooks/hooks'
import { setLoading } from '../../../redux/store/common/slice'
import { getFacilityUsers } from '../../../pages/ProductRequests/api'

const FacilityUsers = () => {
  const user = useSelector(getUser)

  const { state, actions } = usePRSDetailsContext()
  const { data } = state
  const { handleGetPRSData } = actions

  const ability = usePrsDetailsAbility()

  const dispatch = useDispatch()

  const [totalNumber, setTotalNumber] = useState(0)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<TPRSFacilityUsersData[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [tableData, setTableData] = useState<TPRSFacilityUsersData[]>([])
  const [sortParams, setSortParams] = useState<THealthSystemTableRequestData>()
  const [filters, setFilters] = useState<TPRSFacilityUsersFilters>({})
  const [firstLoad, setFirstLoad] = useState(true)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })

  const { id } = useParams()

  const tableDataTokenRef = useRef<CancelTokenSource>()
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const debouncedSearch = useDebounce(searchValue, 500)

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        getTableData({ ...sortParams, ...filters, page: { ...DEFAULT_PAGE } })
        setPageInfo({ ...DEFAULT_PAGE })
      },
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: THealthSystemTableRequestData) => {
    dispatch(setLoading(true))
    if (firstLoad) {
      setFirstLoad(false)
    }
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: THealthSystemUsersParams = { ...filters }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    if (searchValue) {
      params.search = searchValue
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getFacilityUsers(id as string, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            key: i.uuid
          }))
        )
        setSelectedRowKeys([])
        setSelectedRows([])
        setTotalNumber(resp.data.count)
        handleGetPRSData()
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const applyFilters: TPRSFacilityUsersFiltersFunc = (
    field,
    appliedFilters
  ) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }
  useEffect(() => {
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData[0]?.uuid as string])

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }
  const handleSelectRow = (selectedRowKeys, selectedRow) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRow)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  return (
    <div className="table-wrapper">
      <div className="table-wrapper__header">
        {!!selectedRowKeys.length ? (
          <Typography.Body1
            className="table-wrapper__select-title"
            weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
          >
            Selected {selectedRowKeys.length} of {totalNumber}
          </Typography.Body1>
        ) : (
          <Input
            className="allow-clear"
            propsInput={{
              allowClear: { clearIcon: <Close16 /> },
              className: 'rfp-hs-users_search',
              placeholder: PLACEHOLDERS.SEARCH,
              prefix: <Search16 />,
              value: searchValue,
              onChange: (e) => setSearchValue(e.target.value)
            }}
          />
        )}
        <div className="table-wrapper__header_buttons">
          <Can
            I={ACTIONS.EDIT}
            a={subject(SUBJECTS.FACILITY_USERS, { ...data })}
          >
            <>
              <AddAction
                updateAssignedValues={() =>
                  getTableData({ ...sortParams, ...filters })
                }
                selected={selectedRowKeys}
              />
              <DeleteAction
                updateAssignedValues={() =>
                  getTableData({ ...sortParams, ...filters })
                }
                selected={selectedRowKeys}
                selectedUsers={selectedRows}
              />
            </>
          </Can>
        </div>
      </div>
      <Table<TPRSFacilityUsersData>
        dataSource={tableData}
        columns={columns({
          applyFilters,
          filters,
          community: user?.community || ''
        })}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        className="rfp-vendors-data-list-table"
        onChangePage={handleChangePageSize}
        onChange={handleTableChange}
        hasSearchOrFilters={isTableHasParams<TPRSFacilityUsersFilters>(
          debouncedSearch,
          filters
        )}
        // createButton={}
        rowSelection={
          ability.can(
            ACTIONS.EDIT,
            subject(SUBJECTS.FACILITY_USERS, { ...data })
          )
            ? {
                selectedRowKeys,
                onChange: handleSelectRow
              }
            : undefined
        }
      />
    </div>
  )
}

export default FacilityUsers
